// @ts-nocheck
import React, { useEffect, useState } from 'react';
import { useTheme } from '../contexts/ThemeContext';
import { Loader2, CheckCircle, Eye, X } from 'lucide-react';

interface FloatingProgressIndicatorProps {
  isAnalyzing: boolean;
  progress: number;
  currentStage: string;
  symbol: string;
  onViewResults?: () => void;
  onClose?: () => void;
}

const FloatingProgressIndicator: React.FC<FloatingProgressIndicatorProps> = ({
  isAnalyzing,
  progress,
  currentStage,
  symbol,
  onViewResults,
  onClose
}) => {
  const { isDark } = useTheme();
  const [isVisible, setIsVisible] = useState(false);
  const [isCompleted, setIsCompleted] = useState(false);
  const [showCelebration, setShowCelebration] = useState(false);

  useEffect(() => {
    // Show indicator when analysis starts
    if (isAnalyzing) {
      setIsVisible(true);
      setIsCompleted(false);
      setShowCelebration(false);
    }
  }, [isAnalyzing]);

  useEffect(() => {
    // When analysis completes (progress reaches 100)
    if (progress >= 100 && isVisible && !isCompleted) {
      setIsCompleted(true);
      setShowCelebration(true);

      // Hide celebration after 3 seconds
      setTimeout(() => {
        setShowCelebration(false);
      }, 3000);
    }
  }, [progress, isVisible, isCompleted]);

  const handleClose = () => {
    setIsVisible(false);
    if (onClose) onClose();
  };

  const handleViewResults = () => {
    if (onViewResults) onViewResults();
    // Keep indicator visible but maybe scroll to results
  };

  if (!isVisible) return null;

  return (
    <>
      {/* Floating Progress Indicator */}
      <div
        className={`fixed bottom-6 right-6 z-50 transition-all duration-500 ease-out ${
          isCompleted ? 'scale-105' : 'scale-100'
        }`}
        style={{
          animation: isVisible ? 'slideInFromRight 0.4s ease-out' : 'slideOutToRight 0.3s ease-in'
        }}
      >
        <div
          className={`rounded-2xl shadow-2xl backdrop-blur-md border overflow-hidden ${
            isDark
              ? 'bg-zinc-900/95 border-white/20'
              : 'bg-white/95 border-gray-200'
          }`}
          style={{
            minWidth: '320px',
            maxWidth: '400px',
            boxShadow: isDark
              ? '0 20px 60px rgba(0, 0, 0, 0.5), 0 0 40px rgba(59, 130, 246, 0.2)'
              : '0 20px 60px rgba(0, 0, 0, 0.15), 0 0 20px rgba(59, 130, 246, 0.1)'
          }}
        >
          {/* Close Button */}
          <button
            onClick={handleClose}
            className={`absolute top-3 right-3 p-1.5 rounded-lg transition-all duration-200 z-10 ${
              isDark
                ? 'hover:bg-white/10 text-white/60 hover:text-white'
                : 'hover:bg-gray-100 text-gray-500 hover:text-gray-700'
            }`}
          >
            <X className="h-4 w-4" />
          </button>

          {/* Header */}
          <div className={`px-6 py-4 border-b ${
            isDark ? 'border-white/10' : 'border-gray-200'
          }`}>
            <div className="flex items-center gap-3">
              {!isCompleted ? (
                <Loader2 className="h-5 w-5 text-blue-500 animate-spin" />
              ) : (
                <CheckCircle className="h-5 w-5 text-emerald-500" />
              )}
              <div className="flex-1 min-w-0">
                <h3 className={`font-semibold text-sm ${
                  isDark ? 'text-white' : 'text-gray-900'
                }`}>
                  {!isCompleted ? 'Analyzing' : 'Analysis Complete'} {symbol?.toUpperCase()}
                </h3>
                {!isCompleted && currentStage && (
                  <p className={`text-xs mt-0.5 truncate ${
                    isDark ? 'text-white/60' : 'text-gray-600'
                  }`}>
                    {currentStage}
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Progress Bar */}
          {!isCompleted && (
            <div className="px-6 py-4">
              <div className="flex items-center justify-between mb-2">
                <span className={`text-xs font-medium ${
                  isDark ? 'text-white/80' : 'text-gray-700'
                }`}>
                  Progress
                </span>
                <span className={`text-xs font-semibold ${
                  isDark ? 'text-blue-400' : 'text-blue-600'
                }`}>
                  {Math.round(progress)}%
                </span>
              </div>
              <div className={`h-2 rounded-full overflow-hidden ${
                isDark ? 'bg-white/10' : 'bg-gray-200'
              }`}>
                <div
                  className="h-full bg-gradient-to-r from-blue-500 to-purple-500 transition-all duration-500 ease-out rounded-full"
                  style={{ width: `${progress}%` }}
                >
                  <div className="h-full w-full animate-pulse opacity-50 bg-gradient-to-r from-transparent via-white/30 to-transparent" />
                </div>
              </div>
            </div>
          )}

          {/* Completion Message with CTA */}
          {isCompleted && (
            <div className="px-6 py-4">
              {showCelebration && (
                <div className="mb-3 text-center">
                  <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-emerald-500/10 to-cyan-500/10 border border-emerald-500/30">
                    <CheckCircle className="h-4 w-4 text-emerald-500" />
                    <span className={`text-sm font-medium ${
                      isDark ? 'text-emerald-400' : 'text-emerald-600'
                    }`}>
                      All AI agents have finished!
                    </span>
                  </div>
                </div>
              )}

              <button
                onClick={handleViewResults}
                className={`w-full py-3 px-4 rounded-xl font-semibold transition-all duration-200 flex items-center justify-center gap-2 ${
                  isDark
                    ? 'bg-gradient-to-r from-blue-500 to-purple-500 text-white hover:shadow-lg hover:shadow-blue-500/50 hover:scale-[1.02]'
                    : 'bg-gradient-to-r from-blue-600 to-purple-600 text-white hover:shadow-lg hover:shadow-blue-600/30 hover:scale-[1.02]'
                }`}
              >
                <Eye className="h-5 w-5" />
                <span>View Results Now</span>
              </button>

              <p className={`text-xs text-center mt-3 ${
                isDark ? 'text-white/50' : 'text-gray-500'
              }`}>
                Scroll down or click to see the full analysis
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Animations */}
      <style>{`
        @keyframes slideInFromRight {
          from {
            transform: translateX(120%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }

        @keyframes slideOutToRight {
          from {
            transform: translateX(0);
            opacity: 1;
          }
          to {
            transform: translateX(120%);
            opacity: 0;
          }
        }
      `}</style>
    </>
  );
};

export default FloatingProgressIndicator;
